import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  CheckCircle,
  Edit,
  X,
  RotateCcw,
  Lock,
  Unlock,
  Sparkles,
  Check,
  Search,
  GripVertical,
  ArrowDownUp,
} from 'lucide-react';
import { matchesApi } from '../../lib/api';
import { toast } from 'sonner';

export const MatchesTab = ({
  isItalian,
  championships,
  matches,
  matchesTotal,
  selectedChampForMatches,
  setSelectedChampForMatches,
  fetchMatchesForChampionship,
  setMatches,
  loadingMatches,
  loadingMoreMatches,
  editingMatch,
  setEditingMatch,
  matchDateInput,
  setMatchDateInput,
  handleUpdateMatchDate,
  handleCancelMatchReport,
}) => {
  const [reportsLocked, setReportsLocked] = useState(false);
  const [togglingLock, setTogglingLock] = useState(false);
  const [pendingJolly, setPendingJolly] = useState([]);
  const [approvedJolly, setApprovedJolly] = useState([]);
  const [jollyLoading, setJollyLoading] = useState(false);
  const [processingJolly, setProcessingJolly] = useState(null);
  const [editingResultId, setEditingResultId] = useState(null);
  const [tempHomeScore, setTempHomeScore] = useState('');
  const [tempAwayScore, setTempAwayScore] = useState('');
  const [submittingResult, setSubmittingResult] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState(new Set());
  const [bulkDate, setBulkDate] = useState('');
  const [bulkTime, setBulkTime] = useState('22:10');
  const [bulkMatchDay, setBulkMatchDay] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [reorderMode, setReorderMode] = useState(false);
  const [dragInfo, setDragInfo] = useState(null); // {type:'matchday'|'match', day?, matchId?}
  const [dragOver, setDragOver] = useState(null); // {type, day?, matchId?}
  const [reorderSaving, setReorderSaving] = useState(false);

  const fetchPendingJolly = useCallback(async () => {
    try {
      const res = await matchesApi.getPendingJolly();
      setPendingJolly(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const fetchApprovedJolly = useCallback(async () => {
    try {
      const res = await matchesApi.getApprovedJolly();
      setApprovedJolly(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const fetchLockStatus = useCallback(async () => {
    try {
      const res = await matchesApi.getReportsLock();
      setReportsLocked(res.data.locked);
    } catch (err) {
      console.error('Error fetching reports lock status:', err);
    }
  }, []);

  useEffect(() => {
    fetchLockStatus();
    fetchPendingJolly();
    fetchApprovedJolly();
  }, [fetchLockStatus, fetchPendingJolly, fetchApprovedJolly]);

  const handleApproveJolly = async (requestId) => {
    setProcessingJolly(requestId);
    try {
      const res = await matchesApi.approveJolly(requestId);
      toast.success(res.data?.message || (isItalian ? 'Jolly approvato!' : 'Jolly approved!'));
      fetchPendingJolly();
      fetchApprovedJolly();
      if (selectedChampForMatches) fetchMatchesForChampionship(selectedChampForMatches);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
    setProcessingJolly(null);
  };

  const handleRejectJolly = async (requestId) => {
    setProcessingJolly(requestId);
    try {
      const res = await matchesApi.rejectJolly(requestId);
      toast.success(res.data?.message || (isItalian ? 'Jolly rifiutato' : 'Jolly rejected'));
      fetchPendingJolly();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
    setProcessingJolly(null);
  };

  const handleCancelJolly = async (requestId) => {
    if (!window.confirm(isItalian ? 'Sei sicuro di voler annullare questo Jolly? La data della partita verrà ripristinata e il Jolly sarà restituito.' : 'Are you sure you want to cancel this Jolly? The match date will be reverted and the Jolly will be returned.')) return;
    setProcessingJolly(requestId);
    try {
      const res = await matchesApi.cancelJolly(requestId);
      toast.success(res.data?.message || (isItalian ? 'Jolly annullato!' : 'Jolly cancelled!'));
      fetchPendingJolly();
      fetchApprovedJolly();
      if (selectedChampForMatches) fetchMatchesForChampionship(selectedChampForMatches);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
    setProcessingJolly(null);
  };

  const handleToggleLock = async () => {
    setTogglingLock(true);
    try {
      const res = await matchesApi.toggleReportsLock();
      setReportsLocked(res.data.locked);
      toast.success(
        res.data.locked
          ? (isItalian ? 'Report bloccati!' : 'Reports locked!')
          : (isItalian ? 'Report sbloccati!' : 'Reports unlocked!')
      );
    } catch (err) {
      toast.error(isItalian ? 'Errore nel cambio stato report' : 'Error toggling reports lock');
    } finally {
      setTogglingLock(false);
    }
  };
  const handleForceResult = async (matchId) => {
    const home = parseInt(tempHomeScore, 10);
    const away = parseInt(tempAwayScore, 10);
    if (isNaN(home) || isNaN(away) || home < 0 || away < 0) {
      toast.error(isItalian ? 'Inserisci punteggi validi (numeri >= 0)' : 'Enter valid scores (numbers >= 0)');
      return;
    }
    setSubmittingResult(true);
    try {
      const res = await matchesApi.adminSetResult(matchId, home, away);
      toast.success(res.data?.message || (isItalian ? 'Risultato impostato!' : 'Result set!'));
      setEditingResultId(null);
      setTempHomeScore('');
      setTempAwayScore('');
      if (selectedChampForMatches) fetchMatchesForChampionship(selectedChampForMatches);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore nell\'impostare il risultato' : 'Error setting result'));
    } finally {
      setSubmittingResult(false);
    }
  };

  const hasMore = matches.length < matchesTotal;

  // Filter matches by search term (home/away club name, match_id, status, match_day)
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredMatches = normalizedSearch
    ? matches.filter(m => {
        const home = (m.home_club?.name || '').toLowerCase();
        const away = (m.away_club?.name || '').toLowerCase();
        const id = (m.match_id || '').toLowerCase();
        const status = (m.status || '').toLowerCase();
        const day = String(m.match_day || '');
        const leg = (m.leg || '').toLowerCase();
        return (
          home.includes(normalizedSearch) ||
          away.includes(normalizedSearch) ||
          id.includes(normalizedSearch) ||
          status.includes(normalizedSearch) ||
          leg.includes(normalizedSearch) ||
          day === normalizedSearch ||
          `${home} vs ${away}`.includes(normalizedSearch)
        );
      })
    : matches;

  const toggleSelect = (id) => setSelectedMatches(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });
  const toggleSelectAll = () => {
    setSelectedMatches(prev => prev.size === filteredMatches.length ? new Set() : new Set(filteredMatches.map(m => m.match_id)));
  };
  const handleBulkUpdate = async () => {
    if (!selectedMatches.size || (!bulkDate && !bulkMatchDay)) return;
    setBulkSubmitting(true);
    try {
      const payload = { match_ids: [...selectedMatches] };
      if (bulkDate) payload.match_date = new Date(`${bulkDate}T${bulkTime || '22:10'}:00`).toISOString();
      if (bulkMatchDay) payload.match_day = parseInt(bulkMatchDay);
      const res = await matchesApi.bulkUpdateDate(payload);
      toast.success(res.data?.message || 'Aggiornato');
      setSelectedMatches(new Set()); setBulkDate(''); setBulkTime('22:10'); setBulkMatchDay('');
      if (selectedChampForMatches) fetchMatchesForChampionship(selectedChampForMatches);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    setBulkSubmitting(false);
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <span className="px-2 py-0.5 text-xs rounded bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
          {isItalian ? 'Completata' : 'Completed'}
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 text-xs rounded bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30">
        {isItalian ? 'Programmata' : 'Scheduled'}
      </span>
    );
  };

  // ===== DRAG & DROP HANDLERS =====
  // Auto-scroll while dragging near top/bottom edges (HTML5 native DnD does
  // NOT auto-scroll the page, so we listen for `dragover` globally and scroll
  // when the cursor enters edge zones).
  React.useEffect(() => {
    if (!dragInfo) return;
    const EDGE = 80;       // px from edge that triggers scroll
    const SPEED = 18;      // base speed in px per frame
    let raf = null;
    let lastY = null;

    const onGlobalDragOver = (e) => {
      lastY = e.clientY;
      e.preventDefault(); // ensure dropEffect is honored
    };

    const tick = () => {
      if (lastY != null) {
        const vh = window.innerHeight;
        if (lastY < EDGE) {
          const factor = (EDGE - lastY) / EDGE; // 0..1
          window.scrollBy(0, -SPEED * Math.max(factor, 0.2));
        } else if (lastY > vh - EDGE) {
          const factor = (lastY - (vh - EDGE)) / EDGE;
          window.scrollBy(0, SPEED * Math.max(factor, 0.2));
        }
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener('dragover', onGlobalDragOver);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('dragover', onGlobalDragOver);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [dragInfo]);

  const groupedByDay = React.useMemo(() => {
    const map = new Map();
    for (const m of filteredMatches) {
      const md = m.match_day ?? 0;
      if (!map.has(md)) map.set(md, []);
      map.get(md).push(m);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [filteredMatches]);

  const onDragStartMatchday = (e, day) => {
    setDragInfo({ type: 'matchday', day });
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', `md:${day}`); } catch { /* ignore */ }
  };

  const onDragStartMatch = (e, match) => {
    setDragInfo({ type: 'match', matchId: match.match_id, fromDay: match.match_day, match });
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', `m:${match.match_id}`); } catch { /* ignore */ }
    e.stopPropagation();
  };

  const onDragOverDay = (e, day) => {
    if (!dragInfo) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver({ type: 'matchday', day });
  };

  const onDragOverMatch = (e, match) => {
    // Only meaningful when we're dragging a match
    if (!dragInfo || dragInfo.type !== 'match') return;
    if (dragInfo.matchId === match.match_id) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOver({ type: 'match', matchId: match.match_id, day: match.match_day });
  };

  const onDragLeaveDay = () => setDragOver(null);

  const onDropOnMatch = async (e, targetMatch) => {
    e.preventDefault();
    e.stopPropagation();
    const info = dragInfo;
    setDragInfo(null);
    setDragOver(null);
    if (!info || info.type !== 'match' || reorderSaving) return;
    if (info.matchId === targetMatch.match_id) return;
    if (!selectedChampForMatches) return;

    if (info.fromDay === targetMatch.match_day) {
      // Same matchday — nothing to swap on the day axis
      toast.info(isItalian ? 'Le due partite sono già nella stessa giornata' : 'Both matches are already in the same matchday');
      return;
    }

    if (!window.confirm(isItalian
      ? `Scambiare le due partite? "${info.match.home_club?.name || '?'} vs ${info.match.away_club?.name || '?'}" andrà alla Giornata ${targetMatch.match_day}, e "${targetMatch.home_club?.name || '?'} vs ${targetMatch.away_club?.name || '?'}" alla Giornata ${info.fromDay}.`
      : `Swap the two matches? "${info.match.home_club?.name || '?'} vs ${info.match.away_club?.name || '?'}" → MD ${targetMatch.match_day}, "${targetMatch.home_club?.name || '?'} vs ${targetMatch.away_club?.name || '?'}" → MD ${info.fromDay}.`)) return;

    setReorderSaving(true);
    try {
      const res = await matchesApi.swapMatches(info.matchId, targetMatch.match_id);
      toast.success(res.data?.message || (isItalian ? 'Partite scambiate' : 'Matches swapped'));
      fetchMatchesForChampionship(selectedChampForMatches);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore scambio' : 'Swap error'));
    } finally {
      setReorderSaving(false);
    }
  };

  const onDropOnDay = async (e, targetDay) => {
    e.preventDefault();
    const info = dragInfo;
    setDragInfo(null);
    setDragOver(null);
    if (!info || reorderSaving) return;
    if (!selectedChampForMatches) return;

    if (info.type === 'matchday') {
      if (info.day === targetDay) return;
      if (!window.confirm(isItalian
        ? `Scambiare la Giornata ${info.day} con la Giornata ${targetDay}?`
        : `Swap Matchday ${info.day} with Matchday ${targetDay}?`)) return;
      setReorderSaving(true);
      try {
        const res = await matchesApi.swapMatchDays(selectedChampForMatches, info.day, targetDay);
        toast.success(res.data?.message || (isItalian ? 'Giornate scambiate' : 'Matchdays swapped'));
        fetchMatchesForChampionship(selectedChampForMatches);
      } catch (err) {
        toast.error(err.response?.data?.detail || (isItalian ? 'Errore scambio' : 'Swap error'));
      } finally {
        setReorderSaving(false);
      }
      return;
    }

    if (info.type === 'match') {
      if (info.fromDay === targetDay) return;
      // Validation: same team in target day?
      const teamsInTarget = new Set();
      for (const m of filteredMatches) {
        if (m.match_day === targetDay && m.match_id !== info.matchId) {
          teamsInTarget.add(m.home_club_id);
          teamsInTarget.add(m.away_club_id);
        }
      }
      const conflict = teamsInTarget.has(info.match.home_club_id) || teamsInTarget.has(info.match.away_club_id);
      if (conflict) {
        const proceed = window.confirm(isItalian
          ? `Attenzione: una delle due squadre giocherebbe due volte nella Giornata ${targetDay}. Vuoi procedere comunque?`
          : `Warning: one of the teams would play twice in Matchday ${targetDay}. Proceed anyway?`);
        if (!proceed) return;
      }
      setReorderSaving(true);
      try {
        const res = await matchesApi.updateMatchDay(info.matchId, targetDay);
        toast.success(res.data?.message || (isItalian ? 'Partita spostata' : 'Match moved'));
        fetchMatchesForChampionship(selectedChampForMatches);
      } catch (err) {
        toast.error(err.response?.data?.detail || (isItalian ? 'Errore spostamento' : 'Move error'));
      } finally {
        setReorderSaving(false);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-[#0a0d15] border border-white/10"
    >
      <div className="p-4 border-b border-white/10">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Calendar size={20} className="text-[#FFEA00]" />
            <h3 className="font-heading text-lg uppercase tracking-wider text-white">
              {isItalian ? 'Gestione Partite' : 'Match Management'}
            </h3>
          </div>
          <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3">
            <select
              className="w-full md:w-auto bg-[#050508] border border-white/10 text-white px-3 py-2 text-sm"
              value={selectedChampForMatches || ''}
              onChange={(e) => {
                const champId = e.target.value;
                setSelectedChampForMatches(champId);
                if (champId) {
                  fetchMatchesForChampionship(champId);
                } else {
                  setMatches([]);
                }
              }}
              data-testid="match-championship-select"
            >
              <option value="">{isItalian ? '-- Seleziona Campionato --' : '-- Select Championship --'}</option>
              {championships
                .filter(c => c.status === 'in_progress' || c.status === 'completed')
                .map(c => (
                  <option key={c.championship_id} value={c.championship_id}>
                    {c.name} {c.status === 'completed' ? `(${isItalian ? 'Completato' : 'Completed'})` : ''}
                  </option>
                ))}
            </select>
            <Button
              onClick={handleToggleLock}
              disabled={togglingLock}
              data-testid="toggle-reports-lock-btn"
              className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition-all ${
                reportsLocked
                  ? 'bg-[#FF1744]/20 border border-[#FF1744]/50 text-[#FF1744] hover:bg-[#FF1744]/30'
                  : 'bg-[#00E676]/20 border border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/30'
              }`}
            >
              {togglingLock ? (
                <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
              ) : reportsLocked ? (
                <Lock size={16} />
              ) : (
                <Unlock size={16} />
              )}
              {reportsLocked
                ? (isItalian ? 'Report Bloccati' : 'Reports Locked')
                : (isItalian ? 'Report Sbloccati' : 'Reports Unlocked')
              }
            </Button>
          </div>
        </div>
      </div>

      {/* Pending Jolly Requests */}
      {pendingJolly.length > 0 && (
        <div className="m-4 p-4 bg-[#FFEA00]/5 border border-[#FFEA00]/20 rounded" data-testid="pending-jolly-panel">
          <h4 className="flex items-center gap-2 text-[#FFEA00] font-heading text-sm uppercase tracking-wider mb-3">
            <Sparkles size={16} />
            {isItalian ? `Richieste Jolly in Attesa (${pendingJolly.length})` : `Pending Jolly Requests (${pendingJolly.length})`}
          </h4>
          <div className="space-y-2">
            {pendingJolly.map(j => (
              <div key={j.request_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-[#050508] border border-white/10 rounded">
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-white text-sm font-medium truncate" data-testid={`jolly-match-label-${j.request_id}`}>
                    {j.match_label || j.match_id}
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                    <span>
                      <span className="text-[#FFEA00]">{isItalian ? 'Club:' : 'Club:'}</span>{' '}
                      {j.club_name}
                    </span>
                    <span>
                      <span className="text-slate-500">{isItalian ? 'Richiedente:' : 'By:'}</span>{' '}
                      {j.requested_by_name}
                    </span>
                    {j.current_date && (
                      <span>
                        <span className="text-slate-500">{isItalian ? 'Data attuale:' : 'Current:'}</span>{' '}
                        {new Date(j.current_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                      </span>
                    )}
                    <span>
                      <span className="text-[#00F0FF]">{isItalian ? 'Nuova data:' : 'New:'}</span>{' '}
                      {new Date(j.proposed_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {' '}
                      {new Date(j.proposed_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => handleApproveJolly(j.request_id)}
                    disabled={processingJolly === j.request_id}
                    className="bg-[#00E676]/20 border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/30"
                    data-testid={`approve-jolly-${j.request_id}`}
                  >
                    <Check size={14} className="mr-1" />
                    {isItalian ? 'Approva' : 'Approve'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleRejectJolly(j.request_id)}
                    disabled={processingJolly === j.request_id}
                    className="bg-[#FF1744]/20 border border-[#FF1744]/40 text-[#FF1744] hover:bg-[#FF1744]/30"
                    data-testid={`reject-jolly-${j.request_id}`}
                  >
                    <X size={14} className="mr-1" />
                    {isItalian ? 'Rifiuta' : 'Reject'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleCancelJolly(j.request_id)}
                    disabled={processingJolly === j.request_id}
                    className="bg-slate-500/20 border border-slate-500/40 text-slate-400 hover:bg-slate-500/30"
                    data-testid={`cancel-jolly-${j.request_id}`}
                  >
                    <RotateCcw size={14} className="mr-1" />
                    {isItalian ? 'Annulla' : 'Cancel'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved Jolly - Cancellable */}
      {approvedJolly.length > 0 && (
        <div className="m-4 p-4 bg-[#00E676]/5 border border-[#00E676]/20 rounded" data-testid="approved-jolly-panel">
          <h4 className="flex items-center gap-2 text-[#00E676] font-heading text-sm uppercase tracking-wider mb-3">
            <CheckCircle size={16} />
            {isItalian ? `Jolly Approvati (${approvedJolly.length})` : `Approved Jolly (${approvedJolly.length})`}
          </h4>
          <div className="space-y-2">
            {approvedJolly.map(j => (
              <div key={j.request_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-[#050508] border border-white/10 rounded">
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-white text-sm font-medium truncate" data-testid={`approved-jolly-label-${j.request_id}`}>
                    {j.match_label || j.match_id}
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                    <span>
                      <span className="text-[#00E676]">{isItalian ? 'Club:' : 'Club:'}</span>{' '}
                      {j.club_name}
                    </span>
                    {j.original_date && (
                      <span>
                        <span className="text-slate-500">{isItalian ? 'Data originale:' : 'Original:'}</span>{' '}
                        {new Date(j.original_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                      </span>
                    )}
                    <span>
                      <span className="text-[#00F0FF]">{isItalian ? 'Spostata a:' : 'Moved to:'}</span>{' '}
                      {new Date(j.proposed_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {' '}
                      {new Date(j.proposed_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {j.paid && (
                      <span className="text-[#FFEA00]">{isItalian ? 'A pagamento' : 'Paid'}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => handleCancelJolly(j.request_id)}
                    disabled={processingJolly === j.request_id}
                    className="bg-[#FF1744]/20 border border-[#FF1744]/40 text-[#FF1744] hover:bg-[#FF1744]/30"
                    data-testid={`cancel-approved-jolly-${j.request_id}`}
                  >
                    <RotateCcw size={14} className="mr-1" />
                    {isItalian ? 'Annulla Jolly' : 'Cancel Jolly'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loadingMatches ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
        </div>
      ) : matches.length === 0 ? (
        <div className="p-8 text-center text-slate-400">
          {selectedChampForMatches
            ? (isItalian ? 'Nessuna partita per questo campionato' : 'No matches for this championship')
            : (isItalian ? 'Seleziona un campionato per vedere le partite' : 'Select a championship to view matches')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Search bar */}
          <div className="mb-3" data-testid="search-match-wrapper">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={isItalian
                  ? 'Cerca partita (club, giornata, status, ID)...'
                  : 'Search match (club, matchday, status, ID)...'}
                className="pl-10 pr-10 h-10 bg-[#050508] border-white/10 text-white placeholder:text-slate-500"
                data-testid="search-match-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  aria-label="clear search"
                  data-testid="search-match-clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {searchTerm && hasMore && (
              <div className="mt-2 flex items-center justify-between gap-2 p-2 bg-[#FF9100]/10 border border-[#FF9100]/30 rounded text-xs">
                <span className="text-[#FF9100]">
                  {isItalian
                    ? `Cerco solo tra le ${matches.length} partite caricate. Carica tutte le ${matchesTotal} per una ricerca completa.`
                    : `Searching only the ${matches.length} loaded matches. Load all ${matchesTotal} for a full search.`}
                </span>
                <button
                  type="button"
                  onClick={() => fetchMatchesForChampionship(selectedChampForMatches, false, true)}
                  disabled={loadingMoreMatches}
                  className="shrink-0 px-3 py-1 bg-[#FF9100] text-black font-bold uppercase text-[10px] tracking-wider rounded hover:bg-[#FF9100]/80 disabled:opacity-50"
                  data-testid="load-all-matches-for-search"
                >
                  {loadingMoreMatches ? '...' : (isItalian ? 'Carica Tutte' : 'Load All')}
                </button>
              </div>
            )}
            {searchTerm && (
              <div className="mt-1.5 text-xs text-slate-400">
                {filteredMatches.length}/{matches.length} {isItalian ? 'partite' : 'matches'}
              </div>
            )}
          </div>

          {/* Toggle Reorder Mode */}
          <div className="mb-3 flex items-center justify-between gap-2 p-2 bg-[#050508] border border-white/10 rounded">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <ArrowDownUp size={14} className={reorderMode ? 'text-[#FFEA00]' : 'text-slate-500'} />
              <span>{isItalian ? 'Modalità Riordino (drag & drop)' : 'Reorder Mode (drag & drop)'}</span>
              {reorderMode && (
                <span className="text-[#FFEA00] hidden sm:inline">
                  {isItalian
                    ? '— trascina header giornata per scambiare, riga partita per spostare singola'
                    : '— drag matchday header to swap, match row to move single match'}
                </span>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setReorderMode(v => !v)}
              className={`text-xs ${reorderMode
                ? 'bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/40'
                : 'bg-transparent text-slate-400 border border-white/10 hover:bg-white/5'}`}
              data-testid="toggle-reorder-mode"
            >
              {reorderMode ? (isItalian ? 'Disattiva' : 'Disable') : (isItalian ? 'Attiva' : 'Enable')}
            </Button>
          </div>

          {/* Bulk action bar - sticky */}
          {selectedMatches.size > 0 && (
            <div className="p-3 mb-2 bg-[#0a0d15]/95 backdrop-blur border border-[#00F0FF]/30 rounded sticky top-0 z-20 space-y-2" data-testid="bulk-bar">
              <span className="text-sm text-[#00F0FF] font-bold">{selectedMatches.size} {isItalian ? 'selezionate' : 'selected'}</span>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1">{isItalian ? 'Giornata' : 'Day'}</label>
                  <Input type="number" min={1} value={bulkMatchDay} onChange={e => setBulkMatchDay(e.target.value)} className="w-16 h-9 bg-[#050508] border-white/10 text-white text-center font-mono" placeholder="#" data-testid="bulk-match-day" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1">{isItalian ? 'Data' : 'Date'}</label>
                  <Input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} className="h-9 bg-[#050508] border-white/10 text-white" data-testid="bulk-match-date" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1">{isItalian ? 'Ora' : 'Time'}</label>
                  <Input type="time" value={bulkTime} onChange={e => setBulkTime(e.target.value)} className="h-9 bg-[#050508] border-white/10 text-white w-24" data-testid="bulk-match-time" />
                </div>
                <Button size="sm" onClick={handleBulkUpdate} disabled={bulkSubmitting || (!bulkDate && !bulkMatchDay)} className="h-9 bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black font-bold px-4" data-testid="bulk-update-btn">
                  <Check size={14} className="mr-1" />{bulkSubmitting ? '...' : (isItalian ? 'Applica' : 'Apply')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setSelectedMatches(new Set()); setBulkDate(''); setBulkTime('21:00'); setBulkMatchDay(''); }} className="h-9 text-slate-400 hover:text-white">
                  <X size={14} className="mr-1" />{isItalian ? 'Annulla' : 'Clear'}
                </Button>
              </div>
            </div>
          )}
          {reorderMode ? (
            <div className="space-y-3" data-testid="reorder-view">
              {reorderSaving && (
                <div className="text-xs text-[#FFEA00] flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border-2 border-current border-t-transparent rounded-full" />
                  {isItalian ? 'Salvataggio...' : 'Saving...'}
                </div>
              )}
              {groupedByDay.map(([day, dayMatches]) => {
                const isDayDragging = dragInfo?.type === 'matchday' && dragInfo.day === day;
                const isDayDragOver = dragOver?.type === 'matchday' && dragOver.day === day && dragInfo?.day !== day;
                return (
                  <div
                    key={day}
                    className={`border rounded transition-all ${
                      isDayDragOver
                        ? 'border-[#FFEA00] bg-[#FFEA00]/10'
                        : isDayDragging
                          ? 'border-[#FFEA00]/40 opacity-50'
                          : 'border-white/10 bg-[#050508]/50'
                    }`}
                    onDragOver={(e) => onDragOverDay(e, day)}
                    onDragLeave={onDragLeaveDay}
                    onDrop={(e) => onDropOnDay(e, day)}
                    data-testid={`md-section-${day}`}
                  >
                    {/* Matchday header — draggable */}
                    <div
                      draggable
                      onDragStart={(e) => onDragStartMatchday(e, day)}
                      onDragEnd={() => { setDragInfo(null); setDragOver(null); }}
                      className="flex items-center gap-2 px-3 py-2 bg-[#0a0d15] border-b border-white/10 cursor-grab active:cursor-grabbing"
                      data-testid={`md-header-${day}`}
                    >
                      <GripVertical size={16} className="text-[#FFEA00]" />
                      <span className="font-heading text-sm uppercase tracking-wider text-[#FFEA00]">
                        {isItalian ? 'Giornata' : 'Matchday'} {day}
                      </span>
                      <span className="text-xs text-slate-500">({dayMatches.length} {isItalian ? 'partite' : 'matches'})</span>
                    </div>

                    {/* Matches list — each draggable */}
                    <div className="divide-y divide-white/5">
                      {dayMatches.map(match => {
                        const isMatchDragging = dragInfo?.type === 'match' && dragInfo.matchId === match.match_id;
                        const isMatchDragOver = dragOver?.type === 'match' && dragOver.matchId === match.match_id && dragInfo?.matchId !== match.match_id;
                        return (
                          <div
                            key={match.match_id}
                            draggable
                            onDragStart={(e) => onDragStartMatch(e, match)}
                            onDragEnd={() => { setDragInfo(null); setDragOver(null); }}
                            onDragOver={(e) => onDragOverMatch(e, match)}
                            onDrop={(e) => onDropOnMatch(e, match)}
                            className={`flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-grab active:cursor-grabbing ${
                              isMatchDragging ? 'opacity-40' : ''
                            } ${
                              isMatchDragOver ? 'bg-[#FFEA00]/15 ring-1 ring-[#FFEA00]/60' : ''
                            }`}
                            data-testid={`md-row-${match.match_id}`}
                          >
                            <GripVertical size={14} className="text-slate-500 shrink-0" />
                            <span className={`px-2 py-0.5 text-[10px] rounded uppercase shrink-0 ${
                              match.leg === 'andata' ? 'bg-[#00F0FF]/20 text-[#00F0FF]' : 'bg-[#D500F9]/20 text-[#D500F9]'
                            }`}>
                              {match.leg || 'N/A'}
                            </span>
                            <span className="text-sm text-white truncate flex-1 min-w-0">
                              <span className="font-medium">{match.home_club?.name || 'N/A'}</span>
                              <span className="text-slate-500 mx-2">vs</span>
                              <span className="font-medium">{match.away_club?.name || 'N/A'}</span>
                            </span>
                            {match.status === 'completed' && (
                              <span className="bg-[#050508] px-2 py-0.5 rounded text-xs font-mono font-bold border border-white/10 shrink-0">
                                {match.home_score ?? 0}-{match.away_score ?? 0}
                              </span>
                            )}
                            <span className="text-xs text-slate-400 shrink-0 hidden sm:inline">
                              {match.match_date
                                ? new Date(match.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
                                : (isItalian ? 'Da prog.' : 'TBD')}
                            </span>
                            <span className="shrink-0">{getStatusBadge(match.status)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {groupedByDay.length === 0 && (
                <div className="p-8 text-center text-slate-400 text-sm">
                  {isItalian ? 'Nessuna partita' : 'No matches'}
                </div>
              )}
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="w-8 px-2">
                  <input type="checkbox" checked={filteredMatches.length > 0 && selectedMatches.size === filteredMatches.length} onChange={toggleSelectAll} className="accent-[#00F0FF]" data-testid="select-all-matches" />
                </TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Giornata' : 'Day'}</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Partita' : 'Match'}</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{isItalian ? 'Risultato' : 'Result'}</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Status</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Fase' : 'Leg'}</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Data' : 'Date'}</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">{isItalian ? 'Azioni' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMatches.map((match) => {
                const isCompleted = match.status === 'completed';
                return (
                  <TableRow
                    key={match.match_id}
                    className={`border-white/10 hover:bg-white/5 ${isCompleted ? 'bg-[#00E676]/3' : ''} ${selectedMatches.has(match.match_id) ? '!bg-[#00F0FF]/5' : ''}`}
                  >
                    <TableCell className="px-2">
                      <input type="checkbox" checked={selectedMatches.has(match.match_id)} onChange={() => toggleSelect(match.match_id)} className="accent-[#00F0FF]" data-testid={`select-match-${match.match_id}`} />
                    </TableCell>
                    <TableCell className="font-mono text-[#00F0FF]">
                      {match.match_day}
                    </TableCell>
                    <TableCell>
                      <span className="text-white font-medium">{match.home_club?.name || 'N/A'}</span>
                      <span className="text-slate-500 mx-2">vs</span>
                      <span className="text-white font-medium">{match.away_club?.name || 'N/A'}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {editingResultId === match.match_id ? (
                        <div className="flex items-center justify-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            value={tempHomeScore}
                            onChange={(e) => setTempHomeScore(e.target.value)}
                            placeholder="C"
                            className="bg-[#050508] border-white/10 text-white text-center text-sm w-14 h-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            data-testid={`force-home-score-${match.match_id}`}
                          />
                          <span className="text-slate-500 font-bold">-</span>
                          <Input
                            type="number"
                            min="0"
                            value={tempAwayScore}
                            onChange={(e) => setTempAwayScore(e.target.value)}
                            placeholder="O"
                            className="bg-[#050508] border-white/10 text-white text-center text-sm w-14 h-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            data-testid={`force-away-score-${match.match_id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleForceResult(match.match_id)}
                            disabled={submittingResult}
                            className="bg-[#00E676] hover:bg-[#00E676]/80 text-black h-8 px-2"
                            data-testid={`confirm-force-result-${match.match_id}`}
                          >
                            {submittingResult ? (
                              <div className="animate-spin w-3 h-3 border-2 border-current border-t-transparent rounded-full" />
                            ) : (
                              <CheckCircle size={14} />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingResultId(null);
                              setTempHomeScore('');
                              setTempAwayScore('');
                            }}
                            className="text-slate-400 h-8 px-2"
                            data-testid={`cancel-force-result-${match.match_id}`}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ) : isCompleted ? (
                        <span className="bg-[#050508] px-3 py-1 rounded text-sm font-mono font-bold border border-white/10 inline-flex items-center gap-1">
                          <span className={match.home_score > match.away_score ? 'text-[#00E676]' : 'text-white'}>
                            {match.home_score ?? 0}
                          </span>
                          <span className="text-slate-500">-</span>
                          <span className={match.away_score > match.home_score ? 'text-[#00E676]' : 'text-white'}>
                            {match.away_score ?? 0}
                          </span>
                        </span>
                      ) : (
                        <span className="text-slate-500 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(match.status)}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 text-xs rounded uppercase ${
                        match.leg === 'andata'
                          ? 'bg-[#00F0FF]/20 text-[#00F0FF]'
                          : 'bg-[#D500F9]/20 text-[#D500F9]'
                      }`}>
                        {match.leg || 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {editingMatch === match.match_id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="datetime-local"
                            value={matchDateInput}
                            onChange={(e) => setMatchDateInput(e.target.value)}
                            className="bg-[#050508] border-white/10 text-white text-xs w-44"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateMatchDate(match.match_id)}
                            className="bg-[#00E676] hover:bg-[#00E676]/80 text-black h-8 px-2"
                          >
                            <CheckCircle size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingMatch(null);
                              setMatchDateInput('');
                            }}
                            className="text-slate-400 h-8 px-2"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <span className={match.match_date ? 'text-white' : 'text-slate-500 italic'}>
                          {match.match_date
                            ? (
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                                <span className="flex items-center gap-1.5">
                                  <Calendar size={14} className="text-[#0EA5E9]" />
                                  <span className="font-medium text-sm">
                                    {new Date(match.match_date).toLocaleDateString('it-IT', {
                                      day: '2-digit',
                                      month: 'short',
                                    })}
                                  </span>
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <Clock size={14} className="text-[#FFEA00]" />
                                  <span className="text-[#FFEA00] font-mono font-bold text-sm">
                                    {new Date(match.match_date).toLocaleTimeString('it-IT', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </span>
                              </div>
                            )
                            : (isItalian ? 'Da programmare' : 'TBD')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingResultId(match.match_id);
                            setTempHomeScore(isCompleted ? String(match.home_score ?? 0) : '');
                            setTempAwayScore(isCompleted ? String(match.away_score ?? 0) : '');
                          }}
                          className="text-[#D500F9] border-[#D500F9]/30 hover:bg-[#D500F9]/10"
                          data-testid={`force-result-btn-${match.match_id}`}
                        >
                          <Edit size={14} className="mr-1" />
                          {isItalian ? 'Risultato' : 'Result'}
                        </Button>
                        {!isCompleted && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingMatch(match.match_id);
                              setMatchDateInput(match.match_date ? new Date(match.match_date).toISOString().slice(0, 16) : '');
                            }}
                            className="text-[#FFEA00] border-[#FFEA00]/30 hover:bg-[#FFEA00]/10"
                            data-testid={`edit-match-date-${match.match_id}`}
                          >
                            <Edit size={14} className="mr-1" />
                            {isItalian ? 'Data' : 'Date'}
                          </Button>
                        )}
                        {isCompleted && handleCancelMatchReport && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelMatchReport(match.match_id)}
                            className="text-[#FF1744] border-[#FF1744]/30 hover:bg-[#FF1744]/10"
                            data-testid={`cancel-report-${match.match_id}`}
                          >
                            <RotateCcw size={14} className="mr-1" />
                            {isItalian ? 'Annulla Report' : 'Cancel Report'}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          )}
          {hasMore && (
            <button
              onClick={() => fetchMatchesForChampionship(selectedChampForMatches, true)}
              disabled={loadingMoreMatches}
              className="w-full py-3 border-t border-white/10 text-[#00F0FF] text-sm font-bold uppercase tracking-wider hover:bg-white/5 transition-colors disabled:opacity-50"
              data-testid="load-more-admin-matches"
            >
              {loadingMoreMatches
                ? (isItalian ? 'Caricamento...' : 'Loading...')
                : (isItalian ? `Mostra altre 6 (${matchesTotal - matches.length} rimanenti)` : `Show 6 more (${matchesTotal - matches.length} remaining)`)}
            </button>
          )}
          <div className="px-4 py-2 border-t border-white/10 text-right">
            <span className="text-xs text-slate-500">
              {isItalian
                ? `${matches.length} di ${matchesTotal} partite`
                : `${matches.length} of ${matchesTotal} matches`}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};
